"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Flame, Layers, PhoneCall, Radio, Search, Shield, ShieldAlert, ShieldCheck, Sparkles, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const topCapabilities = [{
  id: "explosives-screening",
  iconName: "ShieldAlert",
  title: "Explosives Screening",
  badge: "Field & Checkpoint",
  description: "Rapid optical and spectroscopic identification of military, commercial, and homemade explosive compounds without direct sample contact or package destruction.",
  features: ["Through-barrier Raman and NIR spectroscopic analysis", "Automated library matching against global threat databases", "Clear pass/fail diagnostic readouts for rapid triage"],
  actionText: "Explore Safety Solutions",
  href: "/SafetyAndSecurityPage"
}, {
  id: "border-security",
  iconName: "ShieldCheck",
  title: "Border Security Checkpoints",
  badge: "Interdiction & Customs",
  description: "High-throughput screening technologies designed for customs ports, maritime freight, and international checkpoint inspection workflows.",
  features: ["Direct container inspection without opening or exposure", "Continuous screening of unknown powders, liquids, and pills", "Ruggedised MIL-STD hardware built for all-weather deployment"],
  actionText: "Border Security & Defence",
  href: "/BorderSecurityAndDefencePage"
}, {
  id: "fire-hazmat",
  iconName: "Flame",
  title: "Fire & Hazmat Response",
  badge: "First Responders",
  description: "Field-deployable analytical instruments giving hazmat teams and emergency responders instant verification of toxic industrial chemicals and unknown spills.",
  features: ["Point-of-need detection of hazardous chemical agents", "Immediate hazard classification for tactical evacuation zones", "Decontamination-ready sealed IP67/68 enclosures"],
  actionText: "Law Enforcement & Hazmat",
  href: "/LawEnforcementPublicSafetyPage"
}];
const sectorPathways = [{
  id: "border-defence",
  title: "Border Security & Defence",
  description: "Checkpoint interdiction, trace threat detection, and military-grade spectroscopic field analyzers.",
  href: "/BorderSecurityAndDefencePage",
  tag: "Defence & Customs"
}, {
  id: "safety-security",
  title: "Safety & Security",
  description: "Facility perimeter screening, hazardous material identification, and threat prevention systems.",
  href: "/SafetyAndSecurityPage",
  tag: "Critical Infrastructure"
}, {
  id: "law-enforcement",
  title: "Law Enforcement & Public Safety",
  description: "Tactical narcotics verification, forensic evidence analysis, and hazmat incident command tools.",
  href: "/LawEnforcementPublicSafetyPage",
  tag: "Public Safety"
}, {
  id: "academic-research",
  title: "Academic & Research",
  description: "High-precision molecular spectroscopy instrumentation for advanced analytical research laboratories.",
  href: "/AcademicAndResearchPage",
  tag: "Laboratory & R&D"
}];
const antechServices = [{
  title: "Application Consultation",
  desc: "Method selection, feasibility testing, and standard protocol matching.",
  href: "/ApplicationConsultationPage"
}, {
  title: "Qualification & Lifecycle Services",
  desc: "Installation, operational, and performance qualification (IQ/OQ/PQ) support.",
  href: "/QualificationLifecycleServicesPage"
}, {
  title: "Training & Application Support",
  desc: "Hands-on operator calibration instruction and method development guidance.",
  href: "/TrainingApplicationSupportPage"
}, {
  title: "Preventive Maintenance & Repairs",
  desc: "Certified calibration routines, optical component recertification, and emergency parts.",
  href: "/PreventiveMaintenanceAndRepairsPage"
}];
export default function Capabilities() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "ShieldAlert":
        return <ShieldAlert className="size-6 text-primary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-6 text-primary" />;
      case "Flame":
        return <Flame className="size-6 text-primary" />;
      default:
        return <Shield className="size-6 text-primary" />;
    }
  };
  return <section id="corecapabilitiesandservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 font-medium">
              Core Capabilities & Services
            </Badge>
          </div>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Application-led analytical technology for critical security and regulated operations.
          </h2>
          <p className="mt-4 text-muted-foreground text-base md:text-lg leading-relaxed">
            Antech Solutions provides certified instrumentation, methodological qualification, and
            lifecycle engineering across Ireland and the UK for high-consequence screening environments.
          </p>
        </motion.div>

        {/* 3-Card Tier: Core Threat & Operational Screening */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16 md:mb-20">
          {topCapabilities.map((item, i) => <div key={item.id} data-index={i} className="flex">
              <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="w-full flex">
                <Card className="w-full flex flex-col justify-between border border-border shadow-xs hover:shadow-md transition-[border-color,box-shadow] bg-card">
                  <div>
                    <CardHeader className="space-y-3 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="p-3 rounded-lg bg-secondary border border-border/70 inline-flex items-center justify-center">
                          {getIcon(item.iconName)}
                        </div>
                        <Badge variant="outline" className="text-xs font-normal">
                          {item.badge}
                        </Badge>
                      </div>
                      <CardTitle className="font-default text-xl font-semibold text-foreground pt-2">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0 pb-4">
                      <Separator className="mb-4" />
                      <ul className="space-y-2.5">
                        {item.features.map((feature, featureIndex) => <li key={featureIndex} data-index={featureIndex} className="flex items-start gap-2.5 text-xs md:text-sm text-foreground/90">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2 border-t border-border">
                    <Button asChild variant="ghost" className="w-full justify-between px-2 text-primary hover:text-primary">
                      <Link to={item.href}>
                        <span>{item.actionText}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* 8/4 Tier: Sector Pathways (8 cols) and Antech Services (4 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* 8-Column Sector Section */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="lg:col-span-8 space-y-6">
            <div className="border-b border-border pb-4 flex flex-col sm:flex-row sm:items-end justify-between gap-2">
              <div>
                <h3 className="font-default text-xl md:text-2xl font-semibold text-foreground">
                  Sector Specialisations
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Targeted instrument portfolios configured for compliance and deployment constraints.
                </p>
              </div>
              <Button asChild variant="outline" size="sm" className="self-start sm:self-auto">
                <Link to="/IndustriesPage">
                  <span>All Industries</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {sectorPathways.map((sector, i) => <div key={sector.id} data-index={i}>
                  <Link to={sector.href} className="group block p-5 rounded-lg border border-border bg-card hover:bg-secondary/40 hover:border-primary/40 transition-[border-color,background-color,box-shadow] shadow-2xs h-full flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <Badge variant="secondary" className="text-[11px] font-normal">
                          {sector.tag}
                        </Badge>
                        <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-[color,transform]" />
                      </div>
                      <h4 className="font-default text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                        {sector.title}
                      </h4>
                      <p className="mt-2 text-xs md:text-sm text-muted-foreground leading-relaxed">
                        {sector.description}
                      </p>
                    </div>
                  </Link>
                </div>)}
            </div>

            {/* Quick Consultation Banner */}
            <div className="p-6 rounded-lg bg-secondary border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h4 className="font-default text-sm font-semibold text-secondary-foreground">
                  Need a feasibility trial on your specific unknown substances?
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  We conduct blind sample trials in regulated laboratory facilities.
                </p>
              </div>
              <Button asChild size="sm" variant="default" className="shrink-0">
                <Link to="/DiscussYourApplicationPage">
                  <span>Discuss Your Application</span>
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* 4-Column Services Section */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.15
        }} className="lg:col-span-4">
            <div className="rounded-xl border border-border bg-card p-6 shadow-xs">
              <div className="flex items-center gap-2 mb-2">
                <Wrench className="size-5 text-primary" />
                <h3 className="font-default text-lg font-semibold text-card-foreground">
                  Antech Services List
                </h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Full-lifecycle support and technical validation for instrumentation.
              </p>

              <Separator className="mb-4" />

              <div className="space-y-4">
                {antechServices.map((service, i) => <div key={i} data-index={i} className="group">
                    <Link to={service.href} className="block">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                          {service.title}
                        </span>
                        <ArrowRight className="size-3.5 text-muted-foreground mt-1 group-hover:text-primary group-hover:translate-x-0.5 transition-[color,transform] shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 leading-snug">
                        {service.desc}
                      </p>
                    </Link>
                    {i < antechServices.length - 1 && <Separator className="mt-3.5" />}
                  </div>)}
              </div>

              <div className="mt-6 pt-4 border-t border-border">
                <Button asChild variant="outline" className="w-full text-xs">
                  <Link to="/ServicesPage">
                    <span>View All Services & Support</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}